import { useState, useMemo } from "react";
import stubbedData from "../stubbed_data.json";
import MonthTabs from "./MonthTab";
import PieChartComponent from "./PieChartComponent";
import LineChartComponent from "./YearlyLineGraph";
import AdditionalInfo from "./AdditionalInfo";
import Ratios from "./Ratios";
import StockTable from "./StockTable";
import ReturnsModal from "./ReturnsModal";
import FactorResearch from "./FactorResearch";
import "./dashboard.css";

const FALLBACK_TICKERS = ["TSLA", "MSFT", "IBM", "AAPL", "SPY"];

const normalizeAllocationsHistory = (rawAllocations) => {
	if (!Array.isArray(rawAllocations)) return [];

	return rawAllocations.map((allocationRow) => {
		if (!allocationRow || typeof allocationRow !== "object") return {};

		return Object.entries(allocationRow).reduce(
			(acc, [assetKey, weight], index) => {
				acc[assetKey] = {
					Ticker: FALLBACK_TICKERS[index] || assetKey.toUpperCase(),
					Weight: Number(weight) || 0,
					Returns: 0,
					DailyReturns: [],
				};
				return acc;
			},
			{},
		);
	});
};

const buildMonthSelection = (index, allocationsData, balanceData, esData) => {
	if (
		!Array.isArray(allocationsData) ||
		index < 0 ||
		index >= allocationsData.length
	) {
		return {
			pieData: {},
			tableData: {},
			balance: Array.isArray(balanceData) ? balanceData[index] || 0 : 0,
			shortfall: Array.isArray(esData) ? esData[index] || 0 : 0,
			returnsData: {},
		};
	}

	const currentData = allocationsData[index] || {};
	const returnsData = {};

	if (currentData && typeof currentData === "object") {
		Object.values(currentData).forEach((stock) => {
			if (stock.Ticker && stock.DailyReturns) {
				returnsData[stock.Ticker] = stock.DailyReturns;
			}
		});
	}

	return {
		pieData: currentData,
		tableData: currentData,
		balance: balanceData[index] || 0,
		shortfall: esData[index] || 0,
		returnsData,
	};
};

export default function DashBoard({
	startDate = "2020-03-01",
	endDate = "2020-12-01",
	startingBalance = 10000.0,
}) {
	const months = useMemo(() => {
		if (!startDate || !endDate) return [];
		const start = startDate.split("-").map(Number);
		const end = endDate.split("-").map(Number);
		const labels = [];
		let [currY, currM] = [start[0], start[1]];
		const [endY, endM] = [end[0], end[1]];
		const mNames = [
			"Jan",
			"Feb",
			"Mar",
			"Apr",
			"May",
			"Jun",
			"Jul",
			"Aug",
			"Sep",
			"Oct",
			"Nov",
			"Dec",
		];
		while (currY < endY || (currY === endY && currM <= endM)) {
			labels.push(`${mNames[currM - 1]} '${currY.toString().slice(-2)}`);
			currM++;
			if (currM > 12) {
				currM = 1;
				currY++;
			}
		}
		return labels;
	}, [startDate, endDate]);

	const [selectedMonth, setSelectedMonth] = useState(
		months.length > 0 ? months[0] : "",
	);
	const balanceData = Array.isArray(stubbedData.balance_history)
		? stubbedData.balance_history
		: [];
	const esData = Array.isArray(stubbedData.es_history)
		? stubbedData.es_history
		: [];
	const allocationsData = Array.isArray(stubbedData.stock_history)
		? stubbedData.stock_history
		: normalizeAllocationsHistory(stubbedData.allocations_history);
	const spData = Array.isArray(stubbedData.s_p)
		? stubbedData.s_p
		: balanceData.map(() => startingBalance);
	const ratios = useMemo(
		() => ({
			information: stubbedData.information_ratio,
			trenor: stubbedData.treynor_ratio,
			sharpe: stubbedData.sharpe_ratio,
		}),
		[],
	);
	const initialMonthIndex = months.length > 0 ? 0 : -1;
	const initialSelection =
		initialMonthIndex >= 0
			? buildMonthSelection(
					initialMonthIndex,
					allocationsData,
					balanceData,
					esData,
				)
			: {
					pieData: [],
					tableData: [],
					balance: 0,
					shortfall: 0,
					returnsData: {},
				};
	const [data, setData] = useState(initialSelection);
	const [monthChosen, setMonthChosen] = useState(initialMonthIndex >= 0);
	const [returnsData, setReturnsData] = useState(initialSelection.returnsData);
	const [showGranularLineGraphModal, setShowGranularLineGraphModal] =
		useState(false);
	const [currentView, setCurrentView] = useState("backtest");

	const handleTabSwitch = (e) => {
		setMonthChosen(true);
		setSelectedMonth(e);
		const idx = months.indexOf(e);
		if (idx < 0) {
			return;
		}
		const selection = buildMonthSelection(
			idx,
			allocationsData,
			balanceData,
			esData,
		);
		setData(selection);
		setReturnsData(selection.returnsData);
	};

	return (
		<div className="db-root">
			<nav className="db-nav">
				<div className="db-nav-left">
					<div className="db-logo">
						<img src={`${process.env.PUBLIC_URL}/logo.png`} alt="UBCTG" />
					</div>
					<h1 className="db-title">Dashboard</h1>
					<div className="db-breadcrumb">
						<span className="db-breadcrumb-home">
							<svg
								viewBox="0 0 24 24"
								fill="currentColor"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M6 19h3.692v-5.077h4.616V19H18v-9l-6-4.5L6 10v9Zm-1 1V9.5L12 4l7 5.5V20h-5.692v-5.077h-2.616V20H5Z" />
							</svg>
						</span>
						<span className="db-breadcrumb-sep">/</span>
						<span
							className="db-breadcrumb-link"
							style={{ color: "rgba(255,255,255,0.4)" }}
						>
							Backtest Engine
						</span>
						<span className="db-breadcrumb-sep">/</span>
						<span
							className={
								currentView === "backtest"
									? "db-breadcrumb-active"
									: "db-breadcrumb-link"
							}
							onClick={() => setCurrentView("backtest")}
						>
							Backtest Config
						</span>
						<span className="db-breadcrumb-sep">/</span>
						<span
							className={
								currentView === "factor-research"
									? "db-breadcrumb-active"
									: "db-breadcrumb-link"
							}
							onClick={() => setCurrentView("factor-research")}
						>
							Factor Research
						</span>
					</div>
				</div>
				<div className="db-nav-right">
					<button
						type="button"
						className="db-nav-theme-btn"
						aria-label="Toggle theme"
						disabled
						title="Theme toggle not implemented"
					>
						<svg
							viewBox="0 0 24 24"
							fill="currentColor"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M12 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Zm0-1a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM12 5a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 1 0v2A.5.5 0 0 1 12 5Zm0 16a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 1 0v2a.5.5 0 0 1-.5.5ZM5 12a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 5 12Zm16 0a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5ZM6.636 7.05a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707.707L6.636 7.05Zm9.9 9.9a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707.707l-1.414 1.414Zm0-11.314 1.414 1.414a.5.5 0 0 1-.707.707L15.829 6.343a.5.5 0 0 1 .707-.707ZM5.929 16.95l1.414-1.414a.5.5 0 0 1 .707.707L6.636 17.657a.5.5 0 0 1-.707-.707Z" />
						</svg>
					</button>
					<div className="db-avatar" />
				</div>
			</nav>

			{currentView === "backtest" ? (
				<div className="db-content">
					<div className="db-top-row">
						<div className="db-card db-chart-card">
							<LineChartComponent
								balanceHistory={balanceData}
								spData={spData}
								months={months}
								onMonthClick={handleTabSwitch}
							/>
						</div>
						<div className="db-card db-ratios-card">
							<Ratios
								information={ratios.information}
								trenor={ratios.trenor}
								sharpe={ratios.sharpe}
							/>
						</div>
					</div>

					<div className="db-tabs-row">
						<MonthTabs
							months={months}
							handleTabSwitch={handleTabSwitch}
							activeMonth={selectedMonth}
						/>
					</div>

					{monthChosen ? (
						<div className="db-bottom-row">
							<div className="db-card db-table-card">
								<StockTable data={data.tableData} />
							</div>
							<div className="db-card db-allocation-card">
								<p className="db-section-title">Asset Allocation</p>
								<div className="db-allocation-inner">
									<PieChartComponent pieData={data.pieData} />
									<AdditionalInfo
										balance={(data.balance || 0).toFixed(2)}
										shortfall={(data.shortfall || 0).toFixed(2)}
										onViewReturns={() => setShowGranularLineGraphModal(true)}
									/>
								</div>
							</div>
						</div>
					) : (
						<div className="db-empty">Select a month above to view details</div>
					)}
				</div>
			) : (
				<FactorResearch />
			)}

			<ReturnsModal
				stockData={returnsData}
				showModal={showGranularLineGraphModal}
				onClose={() => setShowGranularLineGraphModal(false)}
				allocations={data.tableData}
				balance={balanceData[months.indexOf(selectedMonth)]}
				selectedMonth={selectedMonth}
			/>
		</div>
	);
}
